import { useFontRender } from "@/components/FontRender";
import MarkdownFormat from "@/components/MarkdownFormat";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useComponent } from "@/hooks/useMicrositeComponent";
import { useAPI } from "@/lib/api";
import {
  IMicrositeComponent,
  MComponentCalendarProps,
} from "@/types/microsite";
import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { MapPinIcon } from "lucide-react";
import dynamic from "next/dynamic";
import { Fragment, useMemo } from "react";

dayjs.extend(utc);
dayjs.extend(tz);

const RenderIcon = dynamic(() => import("@/components/RenderIcon"), {
  ssr: false,
});

const CalendarComponent = {
  kind: "CALENDAR",
  Render: ({
    microsite,
    isBuilder,
    id,
    isFocus,
    data: {
      title,
      description,
      content,
      icon,
      textFont,
      buttonEffect,
      buttonColor,
      textPosition,
      startDate,
      endDate,
      timezone,
      location,
      allDay,
      singleDate,
    },
  }) => {
    const api = useAPI();
    const withIcon = !!icon;
    const { focusTo } = useComponent();

    const { fontStyle, RenderFontStyle } = useFontRender({
      textFont,
    });

    const buttonType = microsite?.style?.buttonType || "elevate";
    const hasButtonShadow = !!microsite?.style?.buttonShadowColor;

    const handleLink = (e) => {
      // if (linkType === "mail") {
      //   e.preventDefault();
      //   contactModal.open();
      //   return;
      // }

      if (isBuilder) {
        e.preventDefault();
        focusTo(id as string);
        return;
      }

      track();
    };

    const track = () => {
      const post = {
        component_id: id,
        action_type: "button",
      };
      api.post("/track/microsite", post).then(() => {
        //...
      });
    };

    const values = useMemo(() => {
      const startLocal = dayjs
        .utc(startDate)
        .tz(timezone, true)
        .local()
        .toDate();
      const endLocal = dayjs.utc(endDate).tz(timezone, true).local().toDate();
      const googleDateTimeFormat = allDay ? "YYYYMMDD" : "YYYYMMDDTHHmmss";

      const google = `https://www.google.com/calendar/render?${new URLSearchParams(
        {
          action: "TEMPLATE",
          text: title,
          dates: `${dayjs(startLocal).format(googleDateTimeFormat)}/${dayjs(
            endLocal
          ).format(googleDateTimeFormat)}`,
          details: description || "",
          location: location || "",
        }
      ).toString()}`;

      const outlook = `https://outlook.office.com/calendar/0/action/compose?${new URLSearchParams(
        {
          allday: allDay ? "true" : "false",
          body: description || "",
          location: location || "",
          subject: title,
          startdt: dayjs(startLocal).toISOString(),
          enddt: dayjs(endLocal).toISOString(),
        }
      ).toString()}`;

      const yahoo = (() => {
        const duration = dayjs(startLocal).diff(dayjs(endLocal), "minute");
        const dHours = Math.floor(duration / 60);
        const dMinutes = duration % 60;
        const durStr = `${dHours < 10 ? "0" : ""}${dHours}${
          dMinutes < 10 ? "0" : ""
        }${dMinutes}`;

        return `https://calendar.yahoo.com/?${new URLSearchParams({
          v: "60",
          view: "d",
          type: "20",
          title: title,
          st: dayjs(startLocal).format("YYYYMMDD[T]HHmmss"),
          et: dayjs(endLocal).format("YYYYMMDD[T]HHmmss"),
          dur: durStr,
          desc: description || "",
          in_loc: location || "",
        })}`;
      })();

      const ics = () => {
        const content = [
          `BEGIN:VCALENDAR`,
          `VERSION:2.0`,
          `BEGIN:VEVENT`,
          `DTSTART:${dayjs(startLocal).format("YYYYMMDDTHHmmss")}`,
          `DTEND:${dayjs(endLocal).format("YYYYMMDDTHHmmss")}`,
          `SUMMARY:${title}`,
          `DESCRIPTION:${description || ""}`,
          `LOCATION:${location || ""}`,
          `UID:${id}`,
          `END:VEVENT`,
          `END:VCALENDAR`,
        ].join("\n");
        const blob = new Blob([content], {
          type: "text/calendar;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${title || "event"}.ics`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      };

      return { google, outlook, yahoo, ics };
    }, [
      title,
      description,
      location,
      startDate,
      endDate,
      timezone,
      allDay,
      id,
    ]);

    const lists = [
      {
        icon: "google-calendar.png",
        alt: "Google Calendar",
        href: values.google,
      },
      { icon: "yahoo.png", alt: "Yahoo Calendar", href: values.yahoo },
      { icon: "outlook.png", alt: "Outlook Calendar", href: values.outlook },
      { icon: "ical.png", alt: "iCal", onClick: values.ics },
    ];

    return (
      <Fragment>
        <div className="p-2 w-full">
          <div
            className="flex flex-col bg-white p-3 rounded-md w-full text-foreground"
            id={`mcomp-${id}`}
            onClick={() => {
              if (isBuilder) {
                focusTo(id as string);
                return;
              }
            }}
          >
            <div className="flex-1">
              <h2 className="font-worksans font-semibold text-lg leading-6">
                {title}
              </h2>
              {location && (
                <h3 className="font-sans text-muted-foreground text-sm leading-5">
                  <MapPinIcon className="inline-block -mt-0.5 mr-0.5 size-3" />
                  {location}
                </h3>
              )}
              {description && (
                <div className="mt-1 font-sans text-sm">
                  <MarkdownFormat>{description}</MarkdownFormat>
                </div>
              )}
            </div>
            <div className="mt-1 w-full text-muted-foreground text-sm">
              {dayjs
                .utc(startDate)
                .tz(timezone, true)
                .local()
                .format(allDay ? "DD MMMM YYYY" : "DD MMMM YYYY HH:mm")}
              {!singleDate && (
                <>
                  {" "}
                  &mdash;{" "}
                  {dayjs
                    .utc(endDate)
                    .tz(timezone, true)
                    .local()
                    .format(allDay ? "DD MMMM YYYY" : "DD MMMM YYYY HH:mm")}
                </>
              )}
            </div>

            <div className="flex items-center gap-2 mt-2">
              <TooltipProvider>
                {lists.map((item, index) => {
                  return (
                    <Tooltip delayDuration={0} key={index}>
                      <TooltipTrigger asChild>
                        <button
                          className="flex justify-center items-center p-0.5 border rounded-md size-8"
                          key={index}
                          onClick={() => {
                            if (item.onClick) {
                              item.onClick();
                              return;
                            }
                            window.open(item.href, "_blank");
                          }}
                        >
                          <img
                            src={"/images/" + item.icon}
                            alt={item.alt}
                            className="w-full"
                          />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent align="start">
                        Add to {item.alt}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </TooltipProvider>
            </div>
          </div>
        </div>
      </Fragment>
    );

    return <Fragment />;
  },
} as IMicrositeComponent<MComponentCalendarProps>;

export default CalendarComponent;
